import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { cx } from '../lib/cx.js';

export interface FieldShellProps {
  label: string;
  /** Quiet guidance under the label, e.g. "Instagram uses the first 5". */
  hint?: string;
  /** Blocks submission. Shown with an icon as well as colour. */
  error?: string;
  /** Allowed, but worth knowing — e.g. a horizontal video going to Reels. */
  warning?: string;
  /** Right-aligned on the label row: a character count, a unit, a counter. */
  trailing?: ReactNode;
  required?: boolean;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * Every control is labelled, and its hint, error and warning are wired to it
 * with aria-describedby so a screen reader hears them as part of the field
 * rather than as loose text nearby (PLAN §13.1).
 */
export function Field({
  label,
  hint,
  error,
  warning,
  trailing,
  required,
  children,
}: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const warnId = warning ? `${id}-warn` : undefined;
  const describedBy = [hintId, errorId, warnId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        {/* Sentence case, in the body face. The uppercase mono label was the
            same treatment as a character count, so a thing you read and a
            thing the machine measured looked identical — and every label on
            the screen looked like every other. Mono now means "measured". */}
        <label htmlFor={id} className="text-sm leading-tight font-medium text-ink">
          {label}
          {/* Muted, not red. In the error colour this reads as "this field is
              already wrong" on a form nobody has touched yet. */}
          {required && <span className="ml-1.5 text-xs font-normal text-muted">required</span>}
        </label>
        {trailing && <span className="font-mono text-2xs tabular-nums text-faint">{trailing}</span>}
      </div>

      {children({ id, describedBy })}

      {hint && (
        <p id={hintId} className="text-xs leading-snug text-muted">
          {hint}
        </p>
      )}
      {warning && (
        <p id={warnId} className="flex gap-1.5 text-xs leading-snug text-warn">
          <span aria-hidden="true">△</span>
          <span>{warning}</span>
        </p>
      )}
      {error && (
        <p id={errorId} className="flex gap-1.5 text-xs leading-snug text-bad">
          <span aria-hidden="true">✕</span>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

const controlBase =
  'w-full rounded-aw border bg-surface px-3 text-ink placeholder:text-faint ' +
  'min-h-[var(--aw-tap)] text-base transition-colors ' +
  // The ring is drawn inside the border rather than as an outline, so a
  // focused field thickens in place instead of nudging its neighbours.
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25';

const controlTone = (invalid: boolean) =>
  invalid ? 'border-bad' : 'border-line hover:border-control';

/**
 * A field with only a baseline under it, used on the sign-in screen.
 *
 * The risk with a ruled field is that it stops looking like somewhere you can
 * type, so the rule is drawn in --aw-control (the 3:1 interactive border)
 * rather than the decorative --aw-line, and focus thickens it to two pixels in
 * the accent while keeping the ring — the same focus language as every boxed
 * control, just on one edge.
 */
const underlineBase =
  'w-full border-0 border-b bg-transparent px-0 text-ink placeholder:text-faint ' +
  'min-h-[var(--aw-tap)] rounded-none text-base transition-colors ' +
  // No ring and no outline suppression. index.css gives the whole app one
  // focus treatment (`:focus-visible`, 2px in --aw-focus), which a `focus:`
  // utility cannot beat on specificity anyway — the first build of this tried,
  // lost, and shipped a field that drew an outline box *and* a ring. The rule
  // darkening to the accent is the field's own contribution; the outline on
  // top of it is the app's, and PLAN §13.1 wants it identical everywhere.
  'focus:border-b-accent';

const underlineTone = (invalid: boolean) =>
  invalid ? 'border-b-bad' : 'border-b-control hover:border-b-ink';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> &
  Omit<FieldShellProps, 'children'> & {
    /** `underline` drops the box for a single rule. Sign-in only. */
    variant?: 'boxed' | 'underline';
    /**
     * An icon inside the field, before the text. Decorative: it repeats what
     * the label already says, so it is hidden from screen readers rather than
     * read out twice.
     */
    leading?: ReactNode;
  };

export function Input({
  label,
  hint,
  error,
  warning,
  trailing,
  required,
  variant = 'boxed',
  leading,
  ...rest
}: InputProps) {
  const invalid = Boolean(error);
  // ReactNode includes 0, which `leading && …` would hand to cx() as a number.
  const hasLeading = Boolean(leading);
  const className = cx(
    variant === 'underline'
      ? cx(underlineBase, underlineTone(invalid))
      : cx(controlBase, controlTone(invalid)),
    // Room for the icon, which is positioned over the field rather than laid
    // out beside it — a flex row would put the focus outline around the icon
    // too, and the outline belongs to the input alone.
    hasLeading ? cx('peer', variant === 'underline' ? 'pl-7' : 'pl-10') : null,
  );

  return (
    <Field
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(warning === undefined ? {} : { warning })}
      {...(trailing === undefined ? {} : { trailing })}
      {...(required === undefined ? {} : { required })}
    >
      {({ id, describedBy }) => {
        const field = (
          <input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid ? true : undefined}
            className={className}
            {...rest}
          />
        );

        if (!hasLeading) return field;

        // The input comes first in the DOM so `peer-focus` can reach the icon;
        // the icon is painted over its left padding.
        return (
          <span className="relative block">
            {field}
            <span
              aria-hidden="true"
              className={cx(
                'pointer-events-none absolute top-1/2 -translate-y-1/2',
                'text-faint transition-colors peer-focus:text-ink',
                variant === 'underline' ? 'left-0' : 'left-3',
              )}
            >
              {leading}
            </span>
          </span>
        );
      }}
    </Field>
  );
}

export type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> &
  Omit<FieldShellProps, 'children'>;

export function Textarea({
  label,
  hint,
  error,
  warning,
  trailing,
  required,
  rows = 3,
  ...rest
}: TextareaProps) {
  return (
    <Field
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(warning === undefined ? {} : { warning })}
      {...(trailing === undefined ? {} : { trailing })}
      {...(required === undefined ? {} : { required })}
    >
      {({ id, describedBy }) => (
        <textarea
          id={id}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={cx(controlBase, controlTone(Boolean(error)), 'py-2.5 leading-relaxed')}
          {...rest}
        />
      )}
    </Field>
  );
}

// Re-exported so every existing `import { Select } from '.../field.js'` keeps
// working. The implementation moved to its own file when it stopped being a
// thin wrapper around a native control and became a listbox of its own.
export { Select, type SelectOption, type SelectProps } from './select.js';
